import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Grid,
} from "@mui/material";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { formatDateTime } from "utils/date";
import EmptyContainer from "../empty_container/EmptyContainer";
import Loading from "../loading/Loading";
import ReminderStatus from "../reminder_status/ReminderStatus";
import CustomTable from "../table/CustomTable";

import { makeStyles } from "@mui/styles";
import {
  REMINDER_ATTEMPTS_TYPE,
  REMINDER_HISTORY_EMAIL_MESSAGE,
  REMINDER_HISTORY_LETTER_MESSAGE,
  REMINDER_NOTES_STATUS_COLOR_MAP,
  REMINDER_NOTIFICATION_TYPE,
} from "static/constants";
import {
  ExpandMore as ExpandMoreIcon,
  WifiOffOutlined,
} from "@mui/icons-material";
import { mapReminderHistoryStatus } from "utils/patientReminder.util";
import { capitalizeFirstLetterOfEachWord } from "utils/string";
const useStyles = makeStyles(() => ({
  container: { width: "758px" },
  detailPanel: {
    backgroundColor: "#F5F5F5",
    padding: "16px 24px",
    margin: "0 16px",
  },
  cardHeader: {
    backgroundColor: "#fff",
    padding: "16px",
    borderBottom: "1px solid #E0E0E0",
    fontSize: "16px",
    fontWeight: 500,
  },
  card: {
    backgroundColor: "#fff",
    padding: "12px 16px",
    borderRadius: "8px",
  },
  errorCard: {
    backgroundColor: "#FDEDED",
    borderRadius: "8px",
    border: "1px solid #EF5350",
    padding: "12px 16px",
  },
  error: {
    padding: "4px 8px",
    color: "#fff",
    backgroundColor: "#EF5350",
    borderRadius: "4px",
    wordBreak: "break-word",
    maxWidth: "100%",
  },
  heading: {
    color: "#333333",
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 500,
  },
  content: {
    color: "#757575",
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 400,
  },
  reminderAttemptInfo: {
    cursor: "pointer",
    transition: "background 0.2s ease",
  },

  reminderAttemptAccordion: {
    padding: "0 !important",
    "&:hover": {
      backgroundColor: "#F5F5F5",
      borderRadius: "8px",
    },
    "&.Mui-expanded": {
      backgroundColor: "#F5F5F5",
      borderRadius: "8px",
    },
  },

  timelineContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginRight: "16px",
    marginTop: "28px",
  },

  timelineCircle: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    zIndex: 1,
  },

  timelineLine: {
    width: 2,
    flexGrow: 1,
    marginTop: 4,
    alignSelf: "center",
  },
}));

const CommunicationLog = ({ data, isLoading }) => {
  const classes = useStyles();
  const [rowSelection, setRowSelection] = useState({});
  const [selectedRowData, setSelectedRowData] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({
    "mrt-row-select": false,
  });
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [reminderAttemptAccordionToggle, setReminderAttemptAccordionToggle] =
    useState(false);
  const [expandedIndex, setExpandedIndex] = useState(-1);

  useEffect(() => {
    if (data?.length > 0 && !isRowSelected) {
      setRowSelection({ 0: true });
      setSelectedRowData(data[0] || {});
    }
  }, [data, selectedRowData]);

  const handleRowSelection = (rowData) => {
    const id = rowData.id;
    setIsRowSelected(true);
    setSelectedRowData(rowData?.original || {});
    setRowSelection({ [id]: !rowSelection[id] });
  };

  const getStatusColor = (status) =>
    REMINDER_NOTES_STATUS_COLOR_MAP[status?.toLowerCase()] || "#ff0000";

  const splitMessageIntoSentences = (msg) => msg?.split(". ") || [];

  const formatReminderLogTitle = (attempt) => {
    const status = mapReminderHistoryStatus(attempt?.status) || "";
    const statusColor = getStatusColor(status?.toLowerCase());

    const type =
      attempt?.phone_type || attempt?.communication_type || attempt?.type;
    const reminderDate =
      type === "Live Call"
        ? formatDateTime(attempt?.call_date)
        : attempt?.created_at;

    const formattedDate =
      type === "Live Call"
        ? new Date(reminderDate).toLocaleDateString()
        : new Date(reminderDate).toLocaleString();

    return (
      <>
        <strong className="mr-2">
          {attempt?.reminder_type ||
            REMINDER_ATTEMPTS_TYPE[type] ||
            attempt?.type}
        </strong>
        • {formattedDate} •&nbsp;
        <span style={{ color: statusColor }}>{status}</span>
      </>
    );
  };

  const renderLinkedText = (sentence, key) => (
    <p key={key} className={sentence.endsWith(".") ? "mb-0" : "mb-2"}>
      {sentence.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
        part.match(/https?:\/\/[^\s]+/) ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </p>
  );

  const renderError = (error, classes) =>
    error && <div className={clsx(classes.error, "mb-2")}>Error: {error}</div>;

  const renderAddress = (attempt, classes) => {
    const address = attempt?.address1 || attempt?.address2;
    return (
      address && (
        <div className="d-flex align-items-center g-1 mt-2">
          <h3 className={classes.heading}>Recipient Address:</h3>
          <p className={classes.content}>{address}</p>
        </div>
      )
    );
  };

  const renderLiveCallDetails = (attempt, classes) => {
    if (attempt?.type !== "Live Call") return null;

    return (
      <>
        {attempt.status === "Failed" && (
          <div className={clsx(classes.error, "mb-2")}>
            Call Status: {attempt?.status}
          </div>
        )}

        <h3 className={clsx(classes.heading, "mb-2")}>Call Details:</h3>

        {["created_by", "duration", "message"].map(
          (field) =>
            attempt[field] && (
              <div key={field} className="d-flex g-1 mt-2">
                <p>
                  {capitalizeFirstLetterOfEachWord(field.replace("_", " "))}:
                </p>
                <p className={classes.content}>{attempt[field]}</p>
              </div>
            )
        )}
      </>
    );
  };

  const renderTimeline = (classes, isLast) => (
    <div className={classes.timelineContainer}>
      <div
        className={classes.timelineCircle}
        style={{ backgroundColor: "#C4C4C4" }}
      />
      {!isLast && (
        <div
          className={classes.timelineLine}
          style={{ backgroundColor: "#C4C4C4" }}
        />
      )}
    </div>
  );

  const renderMessageDetails = (selectedRowData) => {
    let attempts = selectedRowData?.attempts || [selectedRowData];

    if (selectedRowData?.type === "Letter") {
      const latestAttempt = attempts?.[attempts.length - 1];
      attempts = latestAttempt ? [latestAttempt] : attempts;
    }

    return (
      <div>
        {/* Header */}
        <div className={clsx(classes.card, classes.cardHeader)}>
          <span>{selectedRowData?.type || selectedRowData?.reminder_type}</span>
        </div>

        {/* Body */}
        <div style={{ padding: "16px" }}>
          {attempts.map((attempt, i) => {
            let message = attempt?.message_content || attempt?.message;
            if (attempt?.status !== "Failed") {
              if (attempt?.communication_type === "letter") {
                message = REMINDER_HISTORY_LETTER_MESSAGE;
              }
              if (attempt?.communication_type === "email") {
                message = REMINDER_HISTORY_EMAIL_MESSAGE;
              }
            }

            const sentences = splitMessageIntoSentences(message);
            const hasError =
              attempt?.status === "Failed" || Boolean(attempt?.error_message);

            return (
              <div
                key={i}
                style={{ display: "flex", marginTop: "-8px" }}
                onClick={() =>
                  setReminderAttemptAccordionToggle(
                    !reminderAttemptAccordionToggle
                  )
                }
              >
                {/* Timeline */}
                {renderTimeline(classes, attempts.length - 1 === i)}

                {/* Attempt Info */}
                <div
                  className={classes.reminderAttemptInfo}
                  style={{ flex: 1 }}
                >
                  <Accordion
                    className={classes.reminderAttemptAccordion}
                    expanded={expandedIndex === i || attempts.length === 1}
                    onChange={(e, expanded) =>
                      setExpandedIndex(expanded ? i : -1)
                    }
                    sx={{
                      "&": {
                        margin: "6px 0 10px 0 !important",
                        border: "none",
                        boxShadow: "none",
                      },
                      "& .MuiAccordionSummary-content": {
                        margin: 0,
                        padding: 0,
                      },
                      "& .MuiAccordionDetails-root": {
                        padding: "0 6px",
                        margin: "0 0 6px 0",
                      },
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      id={`panel-${i}-header`}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      {formatReminderLogTitle(attempt)}
                    </AccordionSummary>

                    <AccordionDetails>
                      <div
                        className={hasError ? classes.errorCard : classes.card}
                      >
                        {renderError(attempt?.error_message, classes)}

                        {/* Message Text */}
                        {attempt?.type !== "Live Call" && message && (
                          <>
                            <h3 className={clsx(classes.heading, "mb-2")}>
                              Message Details:
                            </h3>

                            {sentences.map(renderLinkedText)}
                          </>
                        )}

                        {renderAddress(attempt, classes)}
                        {renderLiveCallDetails(attempt, classes)}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      {!data?.length && (
        <div style={{ background: "white" }} className="mt-2">
          <EmptyContainer
            noDataText={
              <>
                <div style={{ textAlign: "center" }}>
                  {isLoading && <Loading />}
                  {!isLoading && (
                    <div>
                      <h3 className="empty__title">No Data Found</h3>
                      <p className="empty__content">
                        There are no communication history for this patient.
                      </p>
                    </div>
                  )}
                </div>
              </>
            }
          />
        </div>
      )}

      {data?.length > 0 && (
        <Grid container>
          <Grid
            item
            xs={8}
            style={{
              height: "220px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Card
              style={{
                height: "220px",
                display: "flex",
                flexDirection: "column",
                overflowY: "scroll",
                borderRadius: 0,
                marginLeft: "1px",
                boxShadow: "none",
              }}
            >
              <CustomTable
                data={data || []}
                getRowId={(_, index) => `${index}`}
                options={{
                  enableSelectAll: false,
                  enableRowSelection: true,
                  enableMultiRowSelection: false,
                  enableRowSelectionColumn: false,
                  manualPagination: false,
                  enablePagination: false,
                  enableBottomToolbar: false,
                  state: { rowSelection, columnVisibility },
                  onRowSelectionChange: setRowSelection,
                  onColumnVisibilityChange: setColumnVisibility,
                  muiTableBodyRowProps: ({ row }, index) => ({
                    onClick: (e) => {
                      e.stopPropagation();
                      handleRowSelection(row);
                    },
                    sx: {
                      cursor: "pointer",
                      transition: "background-color 0.2s ease-in-out",
                      "&.Mui-selected": {
                        backgroundColor: "#f5f5f5 !important",
                      },
                    },
                  }),
                  muiTablePaperProps: {
                    sx: {
                      height: "100%",
                      maxHeight: "100%",
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #E0E0E0",
                      borderRadius: 0,
                    },
                  },
                  muiTableContainerProps: {
                    sx: {
                      flex: 1,
                      overflowY: "auto",
                      maxHeight: "100%",
                    },
                  },
                  muiTableBodyProps: {
                    sx: {
                      flex: 1,
                      maxHeight: "100%",
                    },
                  },
                }}
                columns={[
                  {
                    header: "Date",
                    accessorKey: "date",
                    Cell: ({ row }) =>
                      formatDateTime(row?.original?.created_at),
                  },
                  {
                    header: "Type",
                    accessorKey: "type",
                    accessorFn: (rowData) => {
                      const type = rowData?.type || rowData?.reminder_type;

                      return (
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {type} &nbsp;
                          {rowData?.notification_type ===
                            REMINDER_NOTIFICATION_TYPE.disconnected_monitor_reminder && (
                            <WifiOffOutlined
                              className="icon"
                              color="error"
                              fontSize="small"
                            />
                          )}
                        </span>
                      );
                    },
                  },
                  {
                    header: "Status",
                    accessorKey: "status",
                    width: 150,
                    accessorFn: (rowData) => {
                      const status = rowData?.status || rowData?.error_status;

                      return (
                        <ReminderStatus
                          status={mapReminderHistoryStatus(
                            status
                          )?.toLowerCase()}
                        />
                      );
                    },
                  },
                ]}
              />
            </Card>
          </Grid>

          <Grid item xs={4}>
            <Card
              style={{
                height: "220px",
                display: "flex",
                flexDirection: "column",
                marginLeft: "16px",
                overflowY: "scroll",
                borderRadius: 0,
                marginRight: "1px",
                boxShadow: "none",
                border: "1px solid #E0E0E0",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {renderMessageDetails(selectedRowData)}
            </Card>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default CommunicationLog;
